import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { Router } from '@angular/router'
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet'
import { MatDivider } from '@angular/material/divider'
import { MatListModule } from '@angular/material/list'

import { TranslationService } from '../../../../../services/translation.service'
import { BulkProductShareCsvTemplate } from '../../../../../types/productSharingCsvTemplates'

type FieldMapping = {
  systemField: string
  alias: string
}

@Component({
  selector: 'app-product-sharing-field-mapping-sheet',
  imports: [MatListModule, MatDivider],
  templateUrl: './product-sharing-field-mapping-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-sharing-field-mapping-sheet.component.scss',
})
export class ProductSharingFieldMappingSheetComponent {
  data = inject<{
    csvTemplate?: BulkProductShareCsvTemplate
  }>(MAT_BOTTOM_SHEET_DATA)
  router = inject(Router)
  translationService = inject(TranslationService)

  private _bottomSheetRef =
    inject<MatBottomSheetRef<ProductSharingFieldMappingSheetComponent>>(
      MatBottomSheetRef,
    )

  csvTemplate?: BulkProductShareCsvTemplate
  fields: Array<FieldMapping> = []

  constructor() {
    const data = this.data

    if (data.csvTemplate) {
      this.csvTemplate = data.csvTemplate
      this.fields = this.mapCsvTemplateToFieldMappings(data.csvTemplate)
    }
  }

  /**
   * Handles the `keydown` event for keyboard navigation.
   * Allows navigating the list using arrow keys.
   *
   * @param {KeyboardEvent} event The keyboard event triggered when a key is pressed down.
   * @param {number} index The current index of the focused list item.
   * @returns {void}
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    const listItems = document.querySelectorAll('a[mat-list-item]')

    if (event.key === 'ArrowDown') {
      // Prevent default scroll behavior
      event.preventDefault()

      // Focus the next item, if it exists
      if (index < listItems.length - 1)
        (listItems[index + 1] as HTMLElement).focus()
    } else if (event.key === 'ArrowUp') {
      // Prevent default scroll behavior
      event.preventDefault()

      // If the current index is greater than 0, focus on the previous item
      if (index > 0) (listItems[index - 1] as HTMLElement).focus()
    }
  }

  /**
   * Maps a BulkProductShareCsvTemplate into a list of display-friendly field mappings.
   * @param {BulkProductShareCsvTemplate} template The template to map.
   * @returns {FieldMapping[]} Mapped fields.
   */
  mapCsvTemplateToFieldMappings(
    template: BulkProductShareCsvTemplate,
  ): FieldMapping[] {
    return [
      {
        systemField: this.translationService.getProductIdentifierText(),
        alias: template.productIdentifier,
      },
      {
        systemField: this.translationService.getCompanyNameText(),
        alias: template.companyName,
      },
      {
        systemField: this.translationService.getOverrideProductSupplierText(),
        alias: template.overrideProductSupplier,
      },
      {
        systemField:
          this.translationService.getOverrideComponentSuppliersText(),
        alias: template.overrideComponentSuppliers,
      },
    ]
  }
}
